import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  DataTable,
  Button,
  Badge,
  EmptyState,
  Spinner,
  BlockStack,
  InlineStack,
  Text,
  Select,
  TextField
} from '@shopify/polaris';
import axios from 'axios';
import { AppContext } from '../App';

function ShipmentsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { shop, setShop, showToast, API } = useContext(AppContext);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Label Created', value: 'label_created' },
    { label: 'In Transit', value: 'in_transit' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Failed', value: 'failed' }
  ];

  useEffect(() => {
    const shopParam = searchParams.get('shop') || localStorage.getItem('shop');
    if (shopParam) {
      setShop(shopParam);
      fetchShipments(shopParam);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchShipments = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/shipments`, {
        params: { shop: shopDomain }
      });
      setShipments(response.data.shipments || []);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to fetch shipments', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = async (labelUrl, trackingNumber) => {
    if (labelUrl) {
      window.open(labelUrl, '_blank');
    } else {
      try {
        const response = await axios.get(
          `${API}/labels/download/${trackingNumber}`,
          {
            params: { shop: shop },
            responseType: 'blob'
          }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `label_${trackingNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        showToast('Failed to download label', true);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'label_created': { tone: 'info', label: 'Label Created' },
      'in_transit': { tone: 'attention', label: 'In Transit' },
      'delivered': { tone: 'success', label: 'Delivered' },
      'failed': { tone: 'critical', label: 'Failed' },
      'pending': { tone: 'warning', label: 'Pending' }
    };
    const config = statusMap[status] || { tone: 'subdued', label: status || 'Unknown' };
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  const filteredShipments = shipments.filter(shipment => {
    const matchesStatus = statusFilter === 'all' || shipment.status === statusFilter;
    const matchesSearch = !searchQuery || 
      (shipment.tracking_number && shipment.tracking_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (shipment.shopify_order_number && shipment.shopify_order_number.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const rows = filteredShipments.map((shipment) => {
    const createdAt = shipment.created_at ? new Date(shipment.created_at).toLocaleDateString() : 'N/A';
    const cost = shipment.rate_price ? `$${parseFloat(shipment.rate_price).toFixed(2)} ${shipment.currency || 'USD'}` : 'N/A';
    
    return [
      shipment.shopify_order_number || shipment.shopify_order_name || 'N/A',
      shipment.carrier || 'N/A',
      shipment.tracking_number || 'N/A',
      getStatusBadge(shipment.status),
      cost,
      createdAt,
      <InlineStack gap="200">
        {shipment.tracking_number && (
          <Button
            size="slim"
            onClick={() => window.open(`https://track.shippo.com/${shipment.tracking_number}`, '_blank')}
            data-testid={`track-${shipment.tracking_number}-btn`}
          >
            Track
          </Button>
        )}
        {(shipment.label_url || shipment.tracking_number) && (
          <Button
            size="slim"
            onClick={() => handleDownloadLabel(shipment.label_url, shipment.tracking_number)}
            data-testid={`download-${shipment.tracking_number}-btn`}
          >
            Label
          </Button>
        )}
      </InlineStack>
    ];
  });

  if (loading) {
    return (
      <Page title="Shipment History">
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Shipment History"
      backAction={{ onAction: () => navigate(`/orders?shop=${shop}`) }}
      primaryAction={{
        content: 'Orders',
        onAction: () => navigate(`/orders?shop=${shop}`)
      }}
    >
      <BlockStack gap="400">
        <Card>
          <InlineStack gap="400" align="start">
            <div style={{ flex: 1 }}>
              <TextField
                label="Search"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by order or tracking number..."
                clearButton
                onClearButtonClick={() => setSearchQuery('')}
                data-testid="shipment-search-input"
              />
            </div>
            <div style={{ minWidth: '200px' }}>
              <Select
                label="Status"
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                data-testid="status-filter-select"
              />
            </div>
          </InlineStack>
        </Card>

        <Card>
          {filteredShipments.length === 0 ? (
            <EmptyState
              heading="No shipments found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>{shipments.length === 0 ? 'Create your first shipping label to see it here.' : 'No shipments match your filters.'}</p>
            </EmptyState>
          ) : (
            <BlockStack gap="200">
              <Text variant="bodyMd" as="p" tone="subdued">
                Showing {filteredShipments.length} of {shipments.length} shipments
              </Text>
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Order', 'Carrier', 'Tracking #', 'Status', 'Cost', 'Created', 'Actions']}
                rows={rows}
              />
            </BlockStack>
          )}
        </Card>

        {shipments.length > 0 && (
          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">Shipping Summary</Text>
              <InlineStack gap="400">
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">Total Shipments</Text>
                  <Text variant="headingLg" as="p">{shipments.length}</Text>
                </div>
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">In Transit</Text>
                  <Text variant="headingLg" as="p">{shipments.filter(s => s.status === 'in_transit').length}</Text>
                </div>
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">Delivered</Text>
                  <Text variant="headingLg" as="p">{shipments.filter(s => s.status === 'delivered').length}</Text>
                </div>
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">Total Spent</Text>
                  <Text variant="headingLg" as="p">
                    ${shipments.reduce((sum, s) => sum + (parseFloat(s.rate_price) || 0), 0).toFixed(2)}
                  </Text>
                </div>
              </InlineStack>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}

export default ShipmentsPage;
